import React, { useCallback, useMemo, useState } from 'react';
import { UserModel } from '../../../mobx/models/user';
import {
  Check,
  Inputvalue2,
  Invite,
  Members,
  Modalblock,
  Modalhead,
  Name,
  Profile,
  Searchbar,
  Taskbutton
} from '../../../pages/project/details/setting/styles';
import Avatar from '../../avatar/Avatar';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import {
  ButtonSection,
  Headline,
  Icon1,
  Input,
  InputDiv
  // UserDetails, UserName, UserRole
} from './style';
import UserPreferenceSingleton from '../../../helpers/userPreferenceSingleton';
import { ProjectModel } from '../../../mobx/models/project';
import { WorkspaceModel } from '../../../mobx/models/workspace';
import { useDispatch } from 'react-redux';
import { addInvitedMember, getCurrentProjectDetails, removeInvitedMember } from '../../../services/projectServices';
import { PROJECT_ANALYTICS } from '../../../global/analyticsConstants';
import { trackAnalyticActivity } from '../../../services/analyticsService';
import { captureException } from '@sentry/react';
import { UsersWorkspaceInterface } from '../../../interfaces/WorkspaceInterface';
import { removeAgentFromProject } from '../../../services/workspaceService';
import { addAgentToProject } from '../../../services/workspaceService';
import { observer } from 'mobx-react-lite';
import { ProjectTemplateModel } from '../../../mobx/models/projectTemplate';
import { USER_ROLE } from '../../../global/constants';
// import aiAgent1 from '../../../assets/images/ai-agents1.png';
// import aiAgent2 from '../../../assets/images/ai-agents2.png';
interface InviteMemberModalProps {
  onClose: () => void;
  customersUsers?: UserModel[];
  currentProject?: ProjectModel | ProjectTemplateModel;
  onSubModel: () => void;
  setLoading?: (value: boolean) => void;
  workspace?: WorkspaceModel;
  onClickMemberBox?: (userIndex: number, userItem: UserModel | UsersWorkspaceInterface) => void;
}

const InviteMemberModal: React.FC<InviteMemberModalProps> = ({
  onClose,
  customersUsers = [],
  currentProject,
  onSubModel,
  setLoading,
  workspace,
  onClickMemberBox
}) => {
  //State variables
  const [searchResult, setSearchResult] = useState<UserModel[]>([]);
  const [isSearch, setIsSearch] = useState(false);
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState('workspace');
  // Other variables
  const dispatch = useDispatch();

  const workspaceUsers = useMemo(() => {
    return workspace?.users?.filter((user) => user.role !== USER_ROLE.CLIENT);
  }, [workspace?.users]);

  const agentUsers = useMemo(() => {
    return workspace?.users?.filter((user) => user.id.startsWith('agent_')) || [];
  }, [workspace?.users]);

  const getRenderData = useMemo(() => {
    if (isSearch) {
      return searchResult;
    }
    return tab === 'workspace' ? workspaceUsers : tab === 'client' ? customersUsers : agentUsers;
  }, [agentUsers, customersUsers, isSearch, searchResult, tab, workspaceUsers]);

  // handle work space button
  const onClickWorkspaceButton = useCallback((e: React.SyntheticEvent) => {
    e.preventDefault();
    setTab('workspace');
    setQuery('');
    setIsSearch(false);
  }, []);

  // handle client button click
  const onClickClientButton = useCallback((e: React.SyntheticEvent) => {
    e.preventDefault();
    setTab('client');
    setQuery('');
    setIsSearch(false);
  }, []);

  //Agent tab click
  const onClickAgentButton = useCallback((e: React.SyntheticEvent) => {
    e.preventDefault();
    setTab('agent');
    setQuery('');
    setIsSearch(false);
  }, []);

  const searchQuery = useCallback(
    (value: string) => {
      let result;
      if (tab === 'client') {
        result = customersUsers?.filter((item) => item?.name?.toLowerCase().includes(value?.toLowerCase())) || [];
      } else if (tab === 'agent') {
        result = agentUsers?.filter((item) => item?.name?.toLowerCase().includes(value?.toLowerCase())) || [];
      } else {
        result = workspaceUsers?.filter((item) => item?.name?.toLowerCase().includes(value?.toLowerCase())) || [];
      }
      if (result) {
        setSearchResult(result);
        return null;
      }
    },
    [agentUsers, customersUsers, tab, workspaceUsers]
  );

  //Handle search input
  const onChangeSearch = useCallback(
    (e: { target: { value: string } }) => {
      const value = e.target.value;
      setQuery(value);
      if (value) {
        if (!isSearch) setIsSearch(true);
        searchQuery(value);
      } else {
        if (isSearch) setIsSearch(false);
        setSearchResult([]);
      }
    },
    [isSearch, searchQuery]
  );

  const handleCloseModel = useCallback(() => {
    onClose();
    setTab('workspace');
    setQuery('');
    setIsSearch(false);
  }, [onClose]);

  // handle member box click
  const handleMemberClickAction = useCallback(
    async (userIndex: number | undefined, invitedUser: UserModel | UsersWorkspaceInterface) => {
      const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
      try {
        if (currentProject instanceof ProjectTemplateModel) return;
        setLoading?.(true);
        let result;
        let usersClone = JSON.parse(JSON.stringify(currentProject?.users));
        if (invitedUser?.id.startsWith('agent_')) {
          if (userIndex === -1) {
            await dispatch(addAgentToProject(invitedUser?.id, currentProject?.id));
          } else {
            await dispatch(removeAgentFromProject(invitedUser?.id, currentProject?.id));
          }
        } else {
          if (userIndex === -1) {
            const payload = {
              userId: userDetails?.id,
              newUserId: invitedUser?.id,
              companyId: workspace?.id,
              projectId: currentProject?.id
            };
            usersClone = [...(usersClone || []), invitedUser];
            currentProject?.addUser(invitedUser?.id);
            result = await dispatch(addInvitedMember(payload));
            trackAnalyticActivity(PROJECT_ANALYTICS.MEMBER_ADDED);
          } else {
            usersClone.splice(userIndex, 1);
            currentProject?.removeUser(invitedUser?.id);
            result = await dispatch(removeInvitedMember(userDetails?.id, currentProject?.id, invitedUser?.id));
            trackAnalyticActivity(PROJECT_ANALYTICS.MEMBER_REMOVED);
          }
        }
        if (result) {
          await dispatch(getCurrentProjectDetails(workspace?.id, currentProject?.id));
        }
      } catch (e) {
        captureException(e);
        console.log('ERROR', e);
      } finally {
        setLoading?.(false);
      }
    },
    [setLoading, currentProject, workspace?.id, dispatch]
  );

  const renderMemberList = useCallback(
    (renderData: UserModel[] | UsersWorkspaceInterface[]) => {
      return (
        <Modalblock>
          <Searchbar>
            <InputDiv>
              <Input placeholder='Search by name' className='search' value={query} onChange={onChangeSearch} />
              <SVGIcon name='search-icon' width='18' height='18' viewBox='0 0 16 16' className='icon' />
            </InputDiv>
            {((customersUsers?.length ?? 0) > 0 || agentUsers.length > 0) && (
              <ButtonSection>
                <Taskbutton isActive={tab === 'workspace'} onClick={onClickWorkspaceButton}>
                  Workspace
                </Taskbutton>
                {(customersUsers?.length ?? 0) > 0 && (
                  <Taskbutton isActive={tab === 'client'} onClick={onClickClientButton}>
                    Client
                  </Taskbutton>
                )}
                {agentUsers.length > 0 && (
                  <Taskbutton isActive={tab === 'agent'} onClick={onClickAgentButton}>
                    Agents
                  </Taskbutton>
                )}
              </ButtonSection>
            )}
          </Searchbar>
          <Members>
            {renderData?.map((userItem) => {
              const userIndex = currentProject?.users?.findIndex((user) => user?.id === userItem?.id) ?? -1;
              return (
                <Profile
                  key={userItem?.id}
                  onClick={() =>
                    onClickMemberBox
                      ? onClickMemberBox(userIndex, userItem)
                      : handleMemberClickAction(userIndex, userItem)
                  }>
                  <Name>
                    <Avatar
                      imgSrc={userItem?.profile_photo || ''}
                      name={userItem?.name ? userItem.name : 'U N'}
                      size={28}
                      isNotBorder={true}
                    />
                    <p>{userItem?.name}</p>
                  </Name>
                  <Check>
                    <Inputvalue2 type='checkbox' className='checkbox-round' checked={userIndex === -1 ? false : true} />
                  </Check>
                </Profile>
              );
            })}
          </Members>
          <Invite onClick={onSubModel}>Invite New Member</Invite>
        </Modalblock>
      );
    },
    [
      query,
      onChangeSearch,
      customersUsers?.length,
      agentUsers.length,
      tab,
      onClickWorkspaceButton,
      onClickClientButton,
      onClickAgentButton,
      onSubModel,
      currentProject?.users,
      onClickMemberBox,
      handleMemberClickAction
    ]
  );

  return (
    <>
      <Modalhead>
        <Headline>Invite member to {currentProject?.name} project.</Headline>
        <Icon1 onClick={handleCloseModel}>
          <SVGIcon name='close-icon' width='18' height='18' viewBox='0 0 18 18' stroke='var(--text-primary)' />
        </Icon1>
      </Modalhead>
      {renderMemberList(getRenderData || [])}
    </>
  );
};

export default observer(InviteMemberModal);
